// src/pages/Client/ClientsPage.tsx
import { useState, useEffect, useCallback, memo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Trash2, AlertTriangle } from "lucide-react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  clientsApi,
  Client,
  ClientCreatePayload,
  ClientUpdatePayload,
} from "../../api/clients";
import EntityTasks from "../../components/EntityTasks";
import ReferenceField from "../../components/ReferenceField";
import { useAuth } from "../../contexts/AuthContext";
import { getBrowserCoordinates } from "../../api/attendance";
import { useFileDropPaste } from "../../hooks/useFileDropPaste";
import { canDeleteDirectoryEntity } from "../../utils/directoryPermissions";
import {
  directoryValidationSchema,
  gstinRegex,
} from "../Directory/directoryFormValidation";

// Add CSS animations
const addAnimationsCSS = () => {
  if (!document.getElementById("client-page-animations")) {
    const style = document.createElement("style");
    style.id = "client-page-animations";
    style.textContent = `
      @keyframes slide-in {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      .animate-slide-in {
        animation: slide-in 0.3s ease-out;
      }
    `;
    document.head.appendChild(style);
  }
};

type BranchFormData = {
  id?: string;
  branchName: string;
  gstin: string;
  address1: string;
  address2: string;
  pincode: string;
  state: string;
  country: string;
  // Geofence for clocking in at this site. Strings because they come from text
  // inputs; the backend normalises and validates them as a set.
  latitude: string;
  longitude: string;
  radius_meters: string;
};

type FormData = {
  clientName: string;
  email: string;
  phone: string;
  address1: string;
  address2: string;
  state: string;
  country: string;
  pincode: string;
  gstin: string;
  pan: string;
  reference_name: string;
  // Geofence on the ENTITY's own address, independent of its branch sites.
  latitude: string;
  longitude: string;
  radius_meters: string;
  branchDetails: BranchFormData[];
};

const emptyFormData = (): FormData => ({
  clientName: "",
  email: "",
  phone: "",
  address1: "",
  address2: "",
  state: "",
  country: "",
  pincode: "",
  gstin: "",
  pan: "",
  reference_name: "",
  latitude: "",
  longitude: "",
  radius_meters: "",
  branchDetails: [],
});

// Toast component
const Toast = memo(
  ({
    message,
    type,
    onClose,
  }: {
    message: string;
    type: "success" | "error" | "info";
    onClose: () => void;
  }) => {
    useEffect(() => {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }, [onClose]);

    const styles = {
      success: "bg-green-500",
      error: "bg-red-500",
      info: "bg-blue-500",
    };

    const icons = {
      success: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      ),
      error: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      ),
      info: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    };

    return createPortal(
      <div
        className={`fixed bottom-6 right-6 ${styles[type]} text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in`}
        style={{ zIndex: 2147483000 }}
      >
        {icons[type]}
        <span className="text-sm font-medium">{message}</span>
      </div>,
      document.body
    );
  },
);

// Form Input Component with react-hook-form integration
const FormInput = memo(
  ({
    label,
    name,
    control,
    errors,
    type = "text",
    required,
    placeholder,
    maxLength,
    className = "",
    digitsOnly = false,
    inputMode,
  }: {
    label: string;
    name: keyof FormData;
    control: any;
    errors: any;
    type?: string;
    required?: boolean;
    placeholder?: string;
    maxLength?: number;
    className?: string;
    digitsOnly?: boolean;
    inputMode?:
      | "text"
      | "numeric"
      | "decimal"
      | "tel"
      | "email"
      | "url"
      | "search"
      | "none";
  }) => {
    return (
      <div className="cm-field">
        <label className="cm-field-label">
          {label} {required && <span className="cm-field-required">*</span>}
        </label>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type={type}
              maxLength={maxLength}
              inputMode={inputMode}
              placeholder={placeholder || label}
              onChange={(e) => {
                const next = digitsOnly
                  ? e.target.value.replace(/\D+/g, "")
                  : e.target.value;
                field.onChange(next);
              }}
              className={`cm-input ${errors[name] ? "is-error" : ""} ${className}`}
            />
          )}
        />
        {errors[name] && (
          <p className="cm-field-error">{errors[name]?.message}</p>
        )}
      </div>
    );
  },
);

// GST Field Component
const GstField = memo(
  ({
    control,
    errors,
    onFetchGst,
    watchGstin,
  }: {
    control: any;
    errors: any;
    onFetchGst: (gstin: string) => void;
    watchGstin: string;
  }) => {
    const showFetch = watchGstin?.length === 15 && gstinRegex.test(watchGstin);

    return (
      <div className="cm-field">
        <label className="cm-field-label">GSTIN</label>
        <div className="relative">
          <Controller
            name="gstin"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                maxLength={15}
                placeholder="22AAAAA0000A1Z5"
                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                className={`cm-input cm-input-mono cm-input-with-action ${errors.gstin ? "is-error" : ""}`}
              />
            )}
          />
          <button
            type="button"
            disabled={!showFetch}
            aria-hidden={!showFetch}
            tabIndex={showFetch ? 0 : -1}
            onMouseDown={(e) => {
              e.preventDefault();
              if (e.button !== 0) return;
              if (!showFetch) return;
              onFetchGst(watchGstin);
            }}
            onClick={(e) => e.preventDefault()}
            className={`cm-fetch-btn ${showFetch ? "is-visible" : ""}`}
          >
            Fetch
          </button>
        </div>
        {errors.gstin && (
          <p className="cm-field-error">{errors.gstin?.message}</p>
        )}
      </div>
    );
  },
);

// Branch Item Component
const BranchItem = memo(
  ({
    branch,
    index,
    control,
    errors,
    onRemove,
    onFetchGst,
    watchGstin,
  }: {
    branch: any;
    index: number;
    control: any;
    errors: any;
    onRemove: (index: number) => void;
    onFetchGst: (gstin: string, branchIndex: number) => void;
    watchGstin: string;
  }) => {
    const showFetch = watchGstin?.length === 15 && gstinRegex.test(watchGstin);

    return (
      <div className="cm-branch-card">
        <div className="cm-branch-tag">
          <span className="cm-branch-tag-num">{String(index + 1).padStart(2, "0")}</span>
          <span className="cm-branch-tag-label">Branch</span>
        </div>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="cm-branch-remove"
          aria-label={`Remove branch ${index + 1}`}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          <div className="md:col-span-2 cm-field">
            <label className="cm-field-label">
              Branch Name <span className="cm-field-required">*</span>
            </label>
            <Controller
              name={`branchDetails.${index}.branchName`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  placeholder="e.g. Mumbai Office"
                  className="cm-input"
                />
              )}
            />
          </div>

          <div className="cm-field">
            <label className="cm-field-label">GST Number</label>
            <div className="relative">
              <Controller
                name={`branchDetails.${index}.gstin`}
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    maxLength={15}
                    placeholder="22AAAAA0000A1Z5"
                    onChange={(e) =>
                      field.onChange(e.target.value.toUpperCase())
                    }
                    className={`cm-input cm-input-mono cm-input-with-action ${errors.branchDetails?.[index]?.gstin ? "is-error" : ""}`}
                  />
                )}
              />
              <button
                type="button"
                disabled={!showFetch}
                aria-hidden={!showFetch}
                tabIndex={showFetch ? 0 : -1}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (e.button !== 0) return;
                  if (!showFetch) return;
                  onFetchGst(watchGstin, index);
                }}
                onClick={(e) => e.preventDefault()}
                className={`cm-fetch-btn ${showFetch ? "is-visible" : ""}`}
              >
                Fetch
              </button>
            </div>
            {errors.branchDetails?.[index]?.gstin && (
              <p className="cm-field-error">
                {errors.branchDetails[index].gstin?.message}
              </p>
            )}
          </div>

          <div className="cm-field">
            <label className="cm-field-label">Pincode</label>
            <Controller
              name={`branchDetails.${index}.pincode`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  maxLength={6}
                  inputMode="numeric"
                  placeholder="6-digit pincode"
                  onChange={(e) =>
                    field.onChange(
                      e.target.value.replace(/\D/g, "").slice(0, 6),
                    )
                  }
                  className={`cm-input ${errors.branchDetails?.[index]?.pincode ? "is-error" : ""}`}
                />
              )}
            />
            {errors.branchDetails?.[index]?.pincode && (
              <p className="cm-field-error">
                {errors.branchDetails[index].pincode?.message}
              </p>
            )}
          </div>

          <div className="cm-field">
            <label className="cm-field-label">State</label>
            <Controller
              name={`branchDetails.${index}.state`}
              control={control}
              render={({ field }) => (
                <input {...field} placeholder="State" className="cm-input" />
              )}
            />
          </div>

          <div className="cm-field">
            <label className="cm-field-label">Country</label>
            <Controller
              name={`branchDetails.${index}.country`}
              control={control}
              render={({ field }) => (
                <input {...field} placeholder="Country" className="cm-input" />
              )}
            />
          </div>

          <div className="md:col-span-2 cm-field">
            <label className="cm-field-label">Address Line 1</label>
            <Controller
              name={`branchDetails.${index}.address1`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  placeholder="Street address"
                  className="cm-input"
                />
              )}
            />
          </div>

          <div className="md:col-span-2 cm-field">
            <label className="cm-field-label">Address Line 2</label>
            <Controller
              name={`branchDetails.${index}.address2`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  placeholder="Apartment, suite, unit, etc."
                  className="cm-input"
                />
              )}
            />
          </div>
        </div>
      </div>
    );
  },
);

export default function ClientsPage() {
  const { user } = useAuth();
  const canDeleteDirectory = canDeleteDirectoryEntity((user as any)?.role);
  useEffect(() => {
    addAnimationsCSS();
  }, []);

  const navigate = useNavigate();

  const [clients, setClients] = useState<Client[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;

  const [modal, setModal] = useState<"none" | "add" | "edit" | "view">("none");
  const [selected, setSelected] = useState<Client | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  // Compose-email modal (opened from the client detail view).
  const [showEmail, setShowEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Import modal states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [showApiModal, setShowApiModal] = useState(false);
  const [importStep, setImportStep] = useState<
    "credentials" | "loading" | "select" | "preview" | "error"
  >("credentials");
  const [categories, setCategories] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>([
    "Sundry Debtors",
    "Customers",
  ]);
  const [importLoading, setImportLoading] = useState(false);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  // Post-import summary shown inside the modal: which rows were skipped and why.
  const [importResult, setImportResult] = useState<{
    imported: number;
    message: string;
    skipped: { row?: number; name: string; reason: string }[];
  } | null>(null);
  const [apiError, setApiError] = useState("");

  // Drag & drop / Ctrl+V onto the Excel import dropzone. The label+hidden
  // input alone only handles clicks, so a dropped file was being ignored.
  const { dropRef: excelDropRef, isDragOver: excelDragOver } = useFileDropPaste({
    onFiles: (files) => {
      const picked =
        files.find(
          (f) =>
            /\.(xlsx|xls)$/i.test(f.name) ||
            f.type.includes("sheet") ||
            f.type.includes("excel"),
        ) || files[0];
      if (picked) {
        setExcelFile(picked);
        setImportResult(null);
      }
    },
    disabled: !showExcelModal || uploading,
    pasteOnDocument: true,
  });
  const [accessToken, setAccessToken] = useState(null);

  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(directoryValidationSchema),
    defaultValues: emptyFormData(),
    mode: "onChange",
  });

  const [locating, setLocating] = useState(false);

  /** Fill the site geofence from the device standing at it.
   *
   *  Typed coordinates are how a fence ends up pointing at the wrong building:
   *  a pin copied from a map, a truncated decimal or latitude and longitude
   *  swapped all look plausible in a text box and only surface later as a
   *  refused punch naming somewhere else entirely. Reuses the same accuracy
   *  race the clock-in uses, so what is saved here comes from the same source
   *  the geofence will later measure against.
   */
  const useCurrentLocation = async () => {
    setLocating(true);
    try {
      const fix = await getBrowserCoordinates();
      setValue("latitude", String(fix.latitude), { shouldDirty: true });
      setValue("longitude", String(fix.longitude), { shouldDirty: true });
      // Don't overwrite a radius already chosen.
      if (!(watch("radius_meters") ?? "").trim()) {
        setValue("radius_meters", "150", { shouldDirty: true });
      }
      showToast(
        fix.accuracy != null
          ? `Location captured (accurate to about ${Math.round(fix.accuracy)}m). Check the radius covers the building.`
          : "Location captured. Check the radius covers the building.",
        "success",
      );
    } catch (error: any) {
      showToast(error?.message || "Couldn't read this device's location.", "error");
    } finally {
      setLocating(false);
    }
  };

  // Watch GSTIN values for fetch buttons
  const watchGstin = watch("gstin");

  // UseFieldArray for dynamic branches
  const { fields, append, remove } = useFieldArray({
    control,
    name: "branchDetails",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await clientsApi.list({
        search: search || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setClients(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (message: string, type: "success" | "error" | "info") => {
    setToast({ message, type });
  };

  // Send a composed email to the currently-selected client via the backend.
  const handleSendEmail = async () => {
    if (!selected) return;
    const subject = emailSubject.trim();
    const body = emailBody.trim();
    if (!subject || !body) {
      showToast("Subject and message are required.", "error");
      return;
    }
    setSendingEmail(true);
    try {
      await clientsApi.sendEmail(selected.id, { subject, body });
      showToast(`Email sent to ${selected.email}`, "success");
      setShowEmail(false);
      setEmailSubject("");
      setEmailBody("");
    } catch (err: any) {
      showToast(
        err?.response?.data?.detail || err?.message || "Failed to send email",
        "error",
      );
    } finally {
      setSendingEmail(false);
    }
  };

  const fetchGstDetails = async (gstin: string, branchIndex?: number) => {
    const activeEl = document.activeElement as HTMLElement | null;
    const scrollableModal =
      activeEl?.closest?.(".overflow-y-auto") as HTMLElement | null;
    const savedScrollTop = scrollableModal?.scrollTop ?? null;

    const NO_VALIDATE = { shouldValidate: false, shouldDirty: true } as const;

    try {
      const res = await fetch(
        `https://api.startupkhata.com/validateGstNo?gst=${gstin}`,
      );
      const data = await res.json();
      const address =
        data?.response?.principalPlaceOfBusinessFields
          ?.principalPlaceOfBusinessAddress || {};
      const address1 =
        `${address?.buildingNumber || ""} ${address?.buildingName || ""} ${address?.streetName || ""}`.trim();
      const address2 =
        `${address?.location || ""} ${address?.locality || ""} ${address?.districtName || ""}`.trim();

      if (branchIndex !== undefined) {
        // Update branch fields
        setValue(`branchDetails.${branchIndex}.address1`, address1, NO_VALIDATE);
        setValue(`branchDetails.${branchIndex}.address2`, address2, NO_VALIDATE);
        setValue(
          `branchDetails.${branchIndex}.state`,
          address?.stateName || "",
          NO_VALIDATE,
        );
        setValue(
          `branchDetails.${branchIndex}.country`,
          address?.country || "India",
          NO_VALIDATE,
        );
        setValue(
          `branchDetails.${branchIndex}.pincode`,
          address?.pincode || "",
          NO_VALIDATE,
        );
        await trigger(`branchDetails.${branchIndex}`);
      } else {
        // Update main client fields
        setValue("clientName", data.name || "", NO_VALIDATE);
        setValue("pan", gstin.slice(2, -3) || "", NO_VALIDATE);
        setValue("address1", address1, NO_VALIDATE);
        setValue("address2", address2, NO_VALIDATE);
        setValue("state", address?.stateName || "", NO_VALIDATE);
        setValue("country", address?.country || "India", NO_VALIDATE);
        setValue("pincode", address?.pincode || "", NO_VALIDATE);
        await trigger([
          "clientName",
          "pan",
          "address1",
          "address2",
          "state",
          "country",
          "pincode",
        ]);
      }

      if (scrollableModal && savedScrollTop !== null) {
        requestAnimationFrame(() => {
          scrollableModal.scrollTop = savedScrollTop;
        });
      }

      showToast("GST details fetched successfully!", "success");
    } catch (err) {
      console.error("GST fetch failed", err);
      showToast("Failed to fetch GST details", "error");
    }
  };

  const addBranch = () => {
    append({
      branchName: "",
      gstin: "",
      address1: "",
      address2: "",
      pincode: "",
      state: "",
      country: "",
      latitude: "",
      longitude: "",
      radius_meters: "",
    });
  };

  const openAdd = () => {
    reset(emptyFormData());
    setModal("add");
  };

  const openEdit = (c: Client) => {
    reset({
      clientName: c.clientName,
      email: c.email || "",
      phone: c.phone || "",
      address1: c.address1 || "",
      address2: c.address2 || "",
      state: c.state || "",
      country: c.country || "",
      pincode: c.pincode || "",
      gstin: c.gstin || "",
      pan: c.pan || "",
      reference_name: c.reference_name || "",
      latitude: (c as any).latitude || "",
      longitude: (c as any).longitude || "",
      // A radius of 0 is not a fence, so it hydrates as blank rather than "0".
      radius_meters: (c as any).radius_meters ? String((c as any).radius_meters) : "",
      branchDetails: ((c as any).branches || []).map((b: any) => ({
        ...b,
        latitude: b.latitude || "",
        longitude: b.longitude || "",
        radius_meters: b.radius_meters ? String(b.radius_meters) : "",
      })),
    });
    setSelected(c);
    setModal("edit");
  };

  const openView = (c: Client) => {
    setSelected(c);
    setModal("view");
  };

  const closeModal = () => {
    setModal("none");
    setSelected(null);
    reset(emptyFormData());
  };

  const onSave = async (data: FormData) => {
    if (!data.clientName.trim()) {
      showToast("Client name is required", "error");
      return;
    }

    setSaving(true);

    try {
      const payload: any = { clientName: data.clientName.trim() };
      const fields = [
        "email",
        "phone",
        "address1",
        "address2",
        "state",
        "country",
        "pincode",
        "gstin",
        "pan",
      ];
      fields.forEach((k) => {
        if ((data as any)[k]) payload[k] = (data as any)[k];
      });
      payload.branchDetails = data.branchDetails ?? [];

      // NOT folded into the `fields` loop above: that loop skips falsy values,
      // so a cleared coordinate could never be sent and a radius of 0 — the
      // way a fence is removed — would silently vanish. The three are sent as
      // a SET or not at all, because the backend validates them together: a
      // latitude with no radius is not a partial update, it is an invisible
      // fence that nobody can clock in at.
      const geoTouched =
        (data.latitude ?? "").trim() !== "" ||
        (data.longitude ?? "").trim() !== "" ||
        (data.radius_meters ?? "").trim() !== "";
      if (modal === "edit" || geoTouched) {
        payload.latitude = (data.latitude ?? "").trim() || null;
        payload.longitude = (data.longitude ?? "").trim() || null;
        const radius = (data.radius_meters ?? "").trim();
        // 0 means "remove the fence" — a float field has no empty-string
        // sentinel the way the text coordinates do.
        payload.radius_meters = radius === "" ? 0 : Number(radius);
      }

      // Not folded into the `fields` loop above: that loop skips falsy values,
      // which would make clearing a reference impossible. On edit we always
      // send the key ("" clears it); on add we only send a real name.
      if (modal === "edit") {
        payload.reference_name = data.reference_name?.trim() ?? "";
      } else if (data.reference_name?.trim()) {
        payload.reference_name = data.reference_name.trim();
      }

      if (modal === "add") {
        await clientsApi.create(payload as ClientCreatePayload);
        showToast("Client created successfully!", "success");
      } else if (modal === "edit" && selected) {
        await clientsApi.update(selected.id, payload as ClientUpdatePayload);
        showToast("Client updated successfully!", "success");
      }

      closeModal();
      load();
    } catch (e: any) {
      const errorMsg = e?.response?.data?.detail || "Failed to save";
      showToast(errorMsg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id: string) => {
    if (!canDeleteDirectory) {
      showToast("Only an admin can delete clients.", "error");
      return;
    }
    setConfirmDeleteId(id);
  };

  const performDelete = async () => {
    const id = confirmDeleteId;
    if (!id || !canDeleteDirectory) return;
    setDeleting(id);
    try {
      await clientsApi.delete(id);
      showToast("Client deleted successfully!", "success");
      setConfirmDeleteId(null);
      load();
    } catch (err: any) {
      // Surface the backend guard message (e.g. "still linked to 3 tasks")
      // instead of a generic failure, and keep the dialog open so the admin
      // can read why the delete was blocked.
      showToast(err?.response?.data?.detail || "Failed to delete client", "error");
    } finally {
      setDeleting(null);
    }
  };

  const handleTemplateDownload = async () => {
    try {
      setDownloadingTemplate(true);
      await clientsApi.downloadImportTemplate();
    } catch (error) {
      console.error(error);
      showToast("Failed to download template", "error");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleExcelImport = async () => {
    if (!excelFile) {
      showToast("Please select a file", "error");
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("uploaded_file", excelFile); // key must match FastAPI

      const response = await clientsApi.importExcel(formData);

      if (!response.data) {
        throw new Error("Upload failed");
      }

      const data = await response.data;

      // Keep the modal open on a partial import so the skipped rows stay
      // readable; only auto-close when everything went in cleanly.
      const skippedRows = [
        ...(data.duplicates || []),
        ...(data.invalid || []),
      ];
      setImportResult({
        imported: data.imported ?? 0,
        message: data.message || "Excel import completed!",
        skipped: skippedRows,
      });

      if (skippedRows.length === 0) {
        showToast(data.message || "Excel import completed!", "success");
        setShowExcelModal(false);
        setExcelFile(null);
        setImportResult(null);
      }
      load();
    } catch (error) {
      console.error(error);
      showToast("Failed to upload Excel file", "error");
    } finally {
      setUploading(false);
    }
  };

  const closeExcelModal = () => {
    setShowExcelModal(false);
    setExcelFile(null);
    setImportResult(null);
  };

  const resetApiImport = () => {
    setImportStep("loading");
    setApiError("");
    setCategories([]);
    setSelectedCategories(["Sundry Debtors", "Customers"]);
    setImportLoading(false);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const avatarColor = (name: string) => {
    const colors = [
      "from-violet-500 to-purple-600",
      "from-blue-500 to-cyan-600",
      "from-emerald-500 to-green-600",
      "from-orange-500 to-red-500",
      "from-pink-500 to-rose-600",
      "from-indigo-500 to-blue-600",
    ];
    return colors[name.charCodeAt(0) % colors.length];
  };

  const avatarStyle = (name: string): React.CSSProperties => {
    const palettes = [
      { from: 'var(--gt-violet-500)', to: 'var(--gt-purple-600)' }, // violet → purple
      { from: 'var(--gt-blue-500)', to: 'var(--gt-cyan-600)' }, // blue → cyan
      { from: 'var(--gt-emerald-500)', to: 'var(--gt-green-600)' }, // emerald → green
      { from: 'var(--gt-orange-500)', to: 'var(--gt-red-500)' }, // orange → red
      { from: 'var(--gt-pink-500)', to: 'var(--gt-rose-600)' }, // pink → rose
      { from: 'var(--gt-indigo-500)', to: 'var(--gt-blue-600)' }, // indigo → blue
      { from: 'var(--gt-teal-500)', to: 'var(--gt-teal-600)' }, // teal
      { from: 'var(--gt-yellow-500)', to: 'var(--gt-amber-500)' }, // yellow → amber
    ];
    const idx = (name?.charCodeAt(0) || 0) % palettes.length;
    const p = palettes[idx];
    return { background: `linear-gradient(135deg, ${p.from} 0%, ${p.to} 100%)` };
  };

  const avatarBarStyle = (name: string): React.CSSProperties => {
    const palettes = [
      { from: '#8b5cf6', to: '#9333ea' },
      { from: '#3b82f6', to: '#0891b2' },
      { from: '#10b981', to: '#16a34a' },
      { from: '#f97316', to: '#ef4444' },
      { from: '#ec4899', to: '#e11d48' },
      { from: '#6366f1', to: '#2563eb' },
      { from: '#14b8a6', to: '#0d9488' },
      { from: '#eab308', to: '#f59e0b' },
    ];
    const idx = (name?.charCodeAt(0) || 0) % palettes.length;
    const p = palettes[idx];
    return { background: `linear-gradient(90deg, ${p.from} 0%, ${p.to} 100%)` };
  };

  // Runs automatically when modal opens
  const initApiImport = async () => {
    setImportStep("loading");
    setApiError("");

    try {
      // 1. Fetch credentials from your settings API
      const settingsRes = await clientsApi.settings();
      if (!settingsRes.data)
        throw new Error("Failed to fetch credentials from settings.");
      console.log(
        "settingsRes.data",
        settingsRes.data,
        settingsRes.data.email,
        settingsRes.data.password,
      );
      const { email, password } = await settingsRes.data[0]; // adjust field names

      // 2. Login with those credentials
      const loginRes = await fetch("https://api.startupkhata.com/login", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ username: email, password }),
      });
      if (!loginRes.ok)
        throw new Error("Login failed. Check your settings credentials.");
      const { access_token } = await loginRes.json();

      setAccessToken(access_token);

      console.log("access_token", access_token);

      // 3. Fetch branches & categories in parallel
      const [categoryRes] = await Promise.all([
        fetch("https://api.startupkhata.com/ledger-master-category?sale=0", {
          headers: { Authorization: `Bearer ${access_token}` },
        }),
      ]);

      if (!categoryRes.ok) throw new Error("Failed to fetch categories.");

      const categoryData = await categoryRes.json();

      // Adjust based on your API response shape
      setCategories(categoryData.data ?? categoryData);

      setImportStep("select");
    } catch (err) {
      setApiError(err.message || "Something went wrong. Please try again.");
      setImportStep("error");
    }
  };

  // Call initApiImport when modal opens
  const openApiModal = () => {
    setShowApiModal(true);
    initApiImport();
  };

  // Updated handleApiImport
  const handleApiImport = async () => {
    setImportLoading(true);
    try {
      const res = await fetch(
        "https://api.startupkhata.com/hrms/detailed_ledger",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            categories_list: selectedCategories.map(Number),
          }),
        },
      );
      const data = await res.json();
      setLedgerPreview(data); // store response in state
      setImportStep("preview"); // go to preview step
    } catch (err) {
      setApiError("Failed to fetch ledger data.");
      setImportStep("error");
    } finally {
      setImportLoading(false);
    }
  };

  const handleConvertAsClient = async() => {
    const payload = {
      ledgerData: ledgerPreview,
    };

    const response = await clientsApi.importClients(payload);  

    if (!response.data) {
      throw new Error("Error at time of conversion");
    }

    const data = await response.data;
    // The backend skips ledgers already in the directory; `message` carries
    // the imported/skipped split so a repeat import doesn't look silent.
    showToast(data.message || "Import completed", data.imported ? "success" : "error");
    setShowApiModal(false);
    resetApiImport();
    load();
  };

  // Add to your state declarations:
  const [ledgerPreview, setLedgerPreview] = useState(null);
  const allSelected = selectedCategories.length === categories.length;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(categories.map((c) => c.id));
    }
  };

  return (
    <div className="gt-themed gt-shell min-h-screen p-6">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-end flex-wrap gap-4 pb-4" style={{ borderBottom: '1px dashed var(--gt-border-strong)' }}>
          <div>
            <h1 className="gt-page-title">
              Client <em>Management</em>
            </h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              Manage your clients and track <em className="gt-serif-italic">task associations</em>.
            </p>
          </div>
          <div className="flex gap-3 relative">
            {/* Import Button — hidden on mobile to keep header compact */}
            <div className="relative hidden md:block">
              <button
                onClick={() => setShowImportDropdown(!showImportDropdown)}
                className="cm-btn cm-btn-ghost"
              >
                <svg
                  className="w-[18px] h-[18px]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.75}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                Import
                <svg
                  className={`w-3.5 h-3.5 transition-transform ${showImportDropdown ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {showImportDropdown && (
                <div className="cm-dropdown">
                  <button
                    onClick={() => {
                      setShowExcelModal(true);
                      setShowImportDropdown(false);
                    }}
                    className="cm-dropdown-item"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span>Import via <em className="gt-serif-italic">Excel</em></span>
                  </button>
                  <button
                    onClick={() => {
                      setShowApiModal(true);
                      setShowImportDropdown(false);
                      openApiModal();
                    }}
                    className="cm-dropdown-item"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                      />
                    </svg>
                    <span>Import via <em className="gt-serif-italic">API</em></span>
                  </button>
                </div>
              )}
            </div>
            <button onClick={openAdd} className="cm-btn cm-btn-primary">
              <svg
                className="w-[18px] h-[18px]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.75}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              New Client
            </button>
          </div>
        </div>

        {/* Stats — editorial ledger tiles, hidden on mobile */}
        <div className="cm-stats">
          {[
            { eyebrow: "Total", label: "Clients", value: total, accent: "Directory" },
            { eyebrow: "On Page", label: "Visible", value: clients.length, accent: "Current" },
            { eyebrow: "Pages", label: "Paginated", value: totalPages || 1, accent: "Total" },
          ].map((s, i) => (
            <div key={s.label} className="cm-stat-tile">
              <span className="cm-stat-index">{String(i + 1).padStart(2, "0")}</span>
              <div className="cm-stat-body">
                <p className="cm-stat-eyebrow">{s.eyebrow}</p>
                <p className="cm-stat-value">
                  {s.value}
                  <em className="cm-stat-suffix">{s.label}</em>
                </p>
              </div>
              <p className="cm-stat-accent">{s.accent}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="cm-search-row">
        <div className="cm-search">
          <svg
            className="cm-search-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.75}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search by name, email, phone…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="cm-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(""); setPage(1); }}
              className="cm-search-clear"
              aria-label="Clear search"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <span className="cm-search-meta">
          {total} {total === 1 ? "entry" : "entries"} <em className="gt-serif-italic">on file</em>
        </span>
      </div>

      {/* Client Grid */}
      {loading ? (
        <div className="cm-loading">
          <div className="cm-spinner" />
          <p className="cm-loading-text">Loading directory…</p>
        </div>
      ) : clients.length === 0 ? (
        <div className="cm-empty">
          <div className="cm-empty-frame">
            <span className="cm-empty-corner cm-empty-corner-tl" />
            <span className="cm-empty-corner cm-empty-corner-tr" />
            <span className="cm-empty-corner cm-empty-corner-bl" />
            <span className="cm-empty-corner cm-empty-corner-br" />
            <p className="cm-empty-eyebrow">— Empty Directory —</p>
            <h3 className="cm-empty-title">
              {search ? <>Nothing matches <em>that search</em></> : <>No clients <em>on record</em></>}
            </h3>
            <p className="cm-empty-sub">
              {search
                ? "Try a different name, email or phone fragment."
                : "Start your directory by registering the first client below."}
            </p>
            {!search && (
              <button onClick={openAdd} className="cm-btn cm-btn-primary cm-empty-cta">
                <svg
                  className="w-[18px] h-[18px]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 4v16m8-8H4" />
                </svg>
                Register first client
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {clients.map((c) => (
            <div
              key={c.id}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg hover:border-indigo-200 transition-all duration-200 overflow-hidden"
            >
              <div
                className="h-1.5"
                style={avatarBarStyle(c.clientName)}
              />
              <div className="p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-sm flex-shrink-0"
                    style={avatarStyle(c.clientName)}
                  >
                    {c.clientName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 text-base truncate">
                      {c.clientName}
                    </h3>
                    {(c.state || c.country) && (
                      <p className="text-xs text-indigo-600 font-medium truncate">
                        {[c.state, c.country].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5 mb-4">
                  {c.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg
                        className="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="truncate text-xs">{c.email}</span>
                    </div>
                  )}
                  {c.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg
                        className="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      <span className="text-xs">{c.phone}</span>
                    </div>
                  )}
                  {c.gstin && (
                    <span className="inline-block text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-mono">
                      GST: {c.gstin}
                    </span>
                  )}
                  {c.pan && (
                    <span className="inline-block text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-mono">
                      PAN: {c.pan}
                    </span>
                  )}
                  {c.reference_name && (
                    <span className="inline-block text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full font-medium">
                      Ref: {c.reference_name}
                    </span>
                  )}
                  {c.is_linked && (
                    <span
                      className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-medium"
                      title="This organization is on Teamlio — details are managed by them"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      On Teamlio
                    </span>
                  )}
                </div>
                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => openView(c)}
                    className="flex-1 py-1.5 text-xs font-medium text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                  >
                    View
                  </button>
                  <button
                    onClick={() => openEdit(c)}
                    className="flex-1 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    Edit
                  </button>
                  {canDeleteDirectory && (
                    <button
                      onClick={() => handleDelete(c.id)}
                      disabled={deleting === c.id}
                      className="flex-1 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {deleting === c.id ? "…" : "Delete"}
                    </button>
                  )}
                  {/* Shown only when the client is linked AND the backend has
                      exposed cross_org_group_id on the client record.
                      The client serializer does not yet join with
                      chat_conversations, so this button is hidden until that
                      backend change lands. Uses ?conv= convention (Phase 12). */}
                  {c.is_linked && c.cross_org_group_id && (
                    <button
                      type="button"
                      className="gc-btn gc-btn--ghost gc-clients-row__workspace-btn flex-1 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      onClick={() => navigate(`/chat?conv=${c.cross_org_group_id}`)}
                      title="Open shared workspace with this client"
                    >
                      Open workspace
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            ← Prev
          </button>
          <span className="px-4 py-2 text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(modal === "add" || modal === "edit") && createPortal(
        <div
          className="gt-themed cm-modal-backdrop"
          style={{ position: 'fixed', inset: 0 }}
          onClick={closeModal}
        >
          <div
            className="cm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cm-modal-header">
              <div className="cm-modal-eyebrow-row">
                <span className="cm-modal-leaf" />
                <span className="cm-modal-eyebrow">
                  Directory · {modal === "add" ? "New Entry" : "Revise Entry"}
                </span>
              </div>
              <h2 className="cm-modal-title">
                {modal === "add" ? (
                  <>New <em>Client</em></>
                ) : (
                  <>Edit <em>{selected?.clientName}</em></>
                )}
              </h2>
              <p className="cm-modal-sub">
                {modal === "add"
                  ? <>Capture the company's identity, contact, and any <em className="gt-serif-italic">branch offices</em>.</>
                  : <>Refine details below — changes apply across <em className="gt-serif-italic">all task associations</em>.</>}
              </p>
              <button
                type="button"
                onClick={closeModal}
                className="cm-modal-close"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit(onSave)} className="cm-modal-form">
              {/* Linked-org banner: this client mirrors another org's profile
                  and cannot be edited from this side. */}
              {modal === "edit" && selected?.is_linked && (
                <div
                  className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-start gap-3"
                  role="status"
                >
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-emerald-900">This user is on Teamlio.</p>
                    <p className="text-emerald-800/90 leading-snug mt-0.5">
                      Their company profile is the source of truth for these fields and is updated by them, not from here. You can still delete this entry from your directory.
                    </p>
                  </div>
                </div>
              )}

              {/* Wrap the editable body in a fieldset so a single `disabled`
                  flag cascades to every nested input + custom button. */}
              <fieldset
                disabled={modal === "edit" && !!selected?.is_linked}
                className="cm-modal-fieldset"
                style={{ border: 0, padding: 0, margin: 0, display: "contents" }}
              >
              {/* Basic Information */}
              <section className="cm-section">
                <div className="cm-section-head">
                  <span className="cm-section-rule" />
                  <span className="cm-section-eyebrow">i.</span>
                  <h3 className="cm-section-title">
                    Basic <em>Information</em>
                  </h3>
                </div>

                <FormInput
                  label="Client Name"
                  name="clientName"
                  control={control}
                  errors={errors}
                  required
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <GstField
                    control={control}
                    errors={errors}
                    onFetchGst={(gstin) => fetchGstDetails(gstin)}
                    watchGstin={watchGstin}
                  />
                  <FormInput
                    label="PAN"
                    name="pan"
                    control={control}
                    errors={errors}
                    placeholder="AAAAA0000A"
                    maxLength={10}
                    className="font-mono"
                  />
                </div>

                <Controller
                  name="reference_name"
                  control={control}
                  render={({ field }) => (
                    <ReferenceField
                      value={field.value || ""}
                      onChange={field.onChange}
                    />
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label="Email"
                    name="email"
                    control={control}
                    errors={errors}
                    type="email"
                  />
                  <FormInput
                    label="Phone"
                    name="phone"
                    control={control}
                    errors={errors}
                    type="tel"
                  />
                </div>

                <FormInput
                  label="Address Line 1"
                  name="address1"
                  control={control}
                  errors={errors}
                />
                <FormInput
                  label="Address Line 2"
                  name="address2"
                  control={control}
                  errors={errors}
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormInput
                    label="State"
                    name="state"
                    control={control}
                    errors={errors}
                  />
                  <FormInput
                    label="Country"
                    name="country"
                    control={control}
                    errors={errors}
                  />
                  <FormInput
                    label="Pincode"
                    name="pincode"
                    control={control}
                    errors={errors}
                    maxLength={6}
                    digitsOnly
                    inputMode="numeric"
                    placeholder="6-digit pincode"
                  />
                </div>
                {/* Site location — so an employee visiting here can clock in.
                    Optional: a client with no fence simply cannot be punched
                    at, which is how every client behaved before this existed.
                    All three or none; the backend refuses a partial set,
                    because coordinates without a radius are an invisible fence
                    nobody can clock in at. */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Site location (optional)
                    </p>
                    <button
                      type="button"
                      onClick={useCurrentLocation}
                      disabled={locating}
                      className="text-xs font-medium text-blue-600 hover:bg-blue-50 px-2 py-1 rounded disabled:opacity-50"
                    >
                      {locating ? "Locating…" : "Use current location"}
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-500 mb-3">
                    Lets employees clock in and out while visiting. Leave blank
                    if they never work from here.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormInput label="Latitude" name="latitude" control={control} errors={errors} placeholder="28.674257" />
                    <FormInput label="Longitude" name="longitude" control={control} errors={errors} placeholder="77.129646" />
                    <FormInput label="Radius (m)" name="radius_meters" control={control} errors={errors} placeholder="150" inputMode="numeric" />
                  </div>
                </div>
              </section>

              {/* Branch Details Section */}
              <section className="cm-section">
                <div className="cm-section-head">
                  <span className="cm-section-rule" />
                  <span className="cm-section-eyebrow">ii.</span>
                  <h3 className="cm-section-title">
                    Branch <em>Offices</em>
                    <span className="cm-section-count">{String(fields.length).padStart(2, "0")}</span>
                  </h3>
                  <button
                    type="button"
                    onClick={addBranch}
                    className="cm-add-branch"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add branch
                  </button>
                </div>

                <div className="space-y-4">
                  {fields.length === 0 ? (
                    <div className="cm-branch-empty">
                      <p className="cm-branch-empty-eyebrow">— No branches —</p>
                      <p className="cm-branch-empty-text">
                        Add an office to track tasks across <em className="gt-serif-italic">multiple locations</em>.
                      </p>
                    </div>
                  ) : (
                    fields.map((field, index) => {
                      const branchGstin = watch(`branchDetails.${index}.gstin`);
                      return (
                        <BranchItem
                          key={field.id}
                          branch={field}
                          index={index}
                          control={control}
                          errors={errors}
                          onRemove={remove}
                          onFetchGst={(gstin, idx) =>
                            fetchGstDetails(gstin, idx)
                          }
                          watchGstin={branchGstin || ""}
                        />
                      );
                    })
                  )}
                </div>
              </section>

              </fieldset>

              <div className="cm-modal-footer">
                <button
                  type="button"
                  onClick={closeModal}
                  className="cm-btn cm-btn-ghost"
                >
                  {modal === "edit" && selected?.is_linked ? "Close" : "Cancel"}
                </button>
                {!(modal === "edit" && selected?.is_linked) && (
                  <button
                    type="submit"
                    disabled={saving}
                    className="cm-btn cm-btn-primary"
                  >
                    {saving ? (
                      <>
                        <span className="cm-btn-spinner" />
                        Saving…
                      </>
                    ) : modal === "add" ? (
                      <>Save client</>
                    ) : (
                      <>Save changes</>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* View Modal - Keep as is from previous implementation */}
      {modal === "view" && selected && createPortal(
        <div
          className="gt-themed fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          style={{ position: 'fixed', inset: 0 }}
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Fixed top color bar */}
            <div
              className="h-2 rounded-t-2xl flex-shrink-0"
              style={avatarBarStyle(selected.clientName)}
            />

            {/* Fixed header */}
            <div className="flex items-center gap-4 px-6 pt-5 pb-4 flex-shrink-0">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-2xl shadow-md"
                style={avatarStyle(selected.clientName)}
              >
                {selected.clientName.charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selected.clientName}
                </h2>
                {(selected.state || selected.country) && (
                  <p className="text-indigo-600 font-medium">
                    {[selected.state, selected.country]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}
              </div>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto flex-1 px-6 pb-4">
              <div className="space-y-2">
                {[
                  {
                    icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
                    label: "Email",
                    value: selected.email,
                  },
                  {
                    icon: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
                    label: "Phone",
                    value: selected.phone,
                  },
                  {
                    icon: "M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z",
                    label: "Address",
                    value: [
                      selected.address1,
                      selected.address2,
                      selected.state,
                      selected.country,
                      selected.pincode,
                    ]
                      .filter(Boolean)
                      .join(", "),
                  },
                  {
                    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
                    label: "GSTIN",
                    value: selected.gstin,
                  },
                  {
                    icon: "M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2",
                    label: "PAN",
                    value: selected.pan,
                  },
                  {
                    icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
                    label: "Reference",
                    value: selected.reference_name,
                  },
                ]
                  .filter((f) => f.value)
                  .map((f) => (
                    <div
                      key={f.label}
                      className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl"
                    >
                      <svg
                        className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={f.icon}
                        />
                      </svg>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">
                          {f.label}
                        </p>
                        <p className="text-sm text-gray-900">{f.value}</p>
                      </div>
                    </div>
                  ))}
              </div>

              {(selected as any).branches?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-indigo-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    Branches ({(selected as any).branches.length})
                  </h4>
                  <div className="space-y-3">
                    {(selected as any).branches.map(
                      (branch: any, idx: number) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-3">
                          <p className="font-medium text-gray-800 text-sm">
                            {branch.branchName}
                          </p>
                          {branch.gstin && (
                            <p className="text-xs text-gray-500 font-mono mt-1">
                              GST: {branch.gstin}
                            </p>
                          )}
                          {branch.address1 && (
                            <p className="text-xs text-gray-500 mt-1">
                              {branch.address1}
                            </p>
                          )}
                          {branch.pincode && (
                            <p className="text-xs text-gray-500">
                              {branch.pincode}
                            </p>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* Tasks for this client */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <EntityTasks
                  entity={{ id: selected.id, linked_headOfficeID: selected.linked_headOfficeID }}
                  kind="client"
                />
              </div>
            </div>

            {/* Fixed footer buttons */}
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
              {selected.email && (
                <button
                  onClick={() => {
                    setEmailSubject("");
                    setEmailBody("");
                    setModal("none");
                    setShowEmail(true);
                  }}
                  className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Email
                </button>
              )}
              <button
                onClick={closeModal}
                className="flex-1 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Compose Email Modal */}
      {showEmail && selected && createPortal(
        <div
          className="gt-themed fixed inset-0 bg-black/50 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
          style={{ position: 'fixed', inset: 0 }}
          onClick={() => !sendingEmail && setShowEmail(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-2 rounded-t-2xl flex-shrink-0" style={avatarBarStyle(selected.clientName)} />

            <div className="flex items-center justify-between px-6 pt-5 pb-3 flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Send Email</h2>
                <p className="text-sm text-gray-500">to {selected.clientName}</p>
              </div>
              <button
                onClick={() => setShowEmail(false)}
                disabled={sendingEmail}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 pb-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">To</label>
                <div className="px-3 py-2.5 bg-gray-100 border border-gray-200 rounded-xl text-sm text-gray-700">
                  {selected.email}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Subject</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  disabled={sendingEmail}
                  maxLength={300}
                  placeholder="Email subject"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Message</label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  disabled={sendingEmail}
                  rows={8}
                  placeholder="Write your message..."
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <p className="text-xs text-gray-400">
                Sent from Teamlio (noreply@teamlio.ai) — replies to this address are not monitored.
              </p>
            </div>

            <div className="flex gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
                className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingEmail ? "Sending..." : "Send Email"}
              </button>
              <button
                onClick={() => setShowEmail(false)}
                disabled={sendingEmail}
                className="flex-1 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Excel Import Modal */}
      {showExcelModal && createPortal(
        // Drop target is the whole overlay, not just the dashed box: a
        // near-miss drop still lands here instead of the browser navigating
        // away and opening the file.
        <div
          ref={excelDropRef}
          className="gt-themed fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          style={{ position: 'fixed', inset: 0 }}
          onClick={closeExcelModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Import via Excel
                </h2>
                <p className="text-sm text-gray-500">
                  Upload your Excel file with client data
                </p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <button
                onClick={handleTemplateDownload}
                disabled={downloadingTemplate}
                className="w-full py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
              >
                {downloadingTemplate ? "Preparing..." : "Download Sample Excel"}
              </button>
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  excelDragOver
                    ? "border-emerald-500 bg-emerald-50/60"
                    : "border-gray-300 hover:border-indigo-400"
                }`}
              >
                <input
                  type="file"
                  id="excel-file"
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    setExcelFile(e.target.files?.[0] || null);
                    setImportResult(null);
                  }}
                  className="hidden"
                />
                <label htmlFor="excel-file" className="cursor-pointer">
                  <svg
                    className="w-12 h-12 text-gray-400 mx-auto mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-sm text-gray-600">
                    {excelFile
                      ? excelFile.name
                      : excelDragOver
                        ? "Drop the file to attach it"
                        : "Click to upload or drag and drop"}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    .xlsx or .xls files only — or paste with Ctrl/Cmd+V
                  </p>
                </label>
              </div>

              {importResult && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/70 overflow-hidden">
                  <div className="px-4 py-3 border-b border-amber-200">
                    <p className="text-sm font-semibold text-gray-900">
                      {importResult.imported} imported ·{" "}
                      {importResult.skipped.length} skipped
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      These rows were left out — they already exist in your
                      client list:
                    </p>
                  </div>
                  <ul className="max-h-44 overflow-y-auto divide-y divide-amber-100">
                    {importResult.skipped.map((row, i) => (
                      <li
                        key={`${row.name}-${i}`}
                        className="px-4 py-2 flex items-start justify-between gap-3"
                      >
                        <span className="text-sm text-gray-800 break-words">
                          {row.row ? (
                            <span className="text-gray-400 mr-1.5">
                              Row {row.row}
                            </span>
                          ) : null}
                          {row.name || "(no name)"}
                        </span>
                        <span className="shrink-0 text-[11px] font-medium text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">
                          {row.reason}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={closeExcelModal}
                className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
              >
                {importResult ? "Close" : "Cancel"}
              </button>
              <button
                onClick={handleExcelImport}
                disabled={!excelFile || uploading}
                className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 font-medium shadow-md disabled:opacity-50"
              >
                {uploading ? "Importing..." : "Import"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* API Import Modal */}
      {/* {showApiModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowApiModal(false);
            resetApiImport();
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 flex items-center gap-3 sticky top-0 bg-white">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Import via API
                </h2>
                <p className="text-sm text-gray-500">
                  {importStep === "loading" && "Authenticating and fetching data..."}
                  {importStep === "select" && "Select categories to import"}
                  {importStep === "error" && "Something went wrong"}
                </p>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {importStep === "loading" && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
                  <p className="text-gray-600">Connecting to StartupKhata...</p>
                </div>
              )}

              {importStep === "error" && (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-700 font-medium text-center">
                    {apiError}
                  </p>
                  <button
                    onClick={initApiImport}
                    className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-100"
                  >
                    Retry
                  </button>
                </div>
              )}

              {importStep === "select" && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Select Categories
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-xl p-3">
                      {categories.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          No categories available
                        </div>
                      ) : (
                        categories.map((category) => (
                          <label
                            key={category.id}
                            className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(
                                category.label,
                              )}
                              onChange={() =>
                                setSelectedCategories((prev) =>
                                  prev.includes(category.label)
                                    ? prev.filter((c) => c !== category.label)
                                    : [...prev, category.label],
                                )
                              }
                              className="w-4 h-4 text-indigo-600 rounded"
                            />
                            <span className="text-sm text-gray-700">
                              {category.label}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {importStep === "select" && (
              <div className="p-6 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
                <button
                  onClick={() => {
                    setShowApiModal(false);
                    resetApiImport();
                  }}
                  className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApiImport}
                  disabled={importLoading}
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 font-medium shadow-md disabled:opacity-50"
                >
                  {importLoading ? "Importing..." : "Import"}
                </button>
              </div>
            )}
          </div>
        </div>
      )} */}

      {showApiModal && createPortal(
        <div
          className="gt-themed fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          style={{ position: 'fixed', inset: 0 }}
          onClick={() => {
            setShowApiModal(false);
            resetApiImport();
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex items-center gap-3 sticky top-0 bg-white z-10">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Import via API
                </h2>
                <p className="text-sm text-gray-500">
                  {importStep === "loading" &&
                    "Authenticating and fetching data..."}
                  {importStep === "select" && "Select categories to import"}
                  {importStep === "preview" && "Preview imported data"}
                  {importStep === "error" && "Something went wrong"}
                </p>
              </div>

              {/* Step indicator */}
              {(importStep === "select" || importStep === "preview") && (
                <div className="ml-auto flex items-center gap-1.5">
                  <div
                    className={`w-2 h-2 rounded-full ${importStep === "select" ? "bg-indigo-600" : "bg-indigo-200"}`}
                  />
                  <div
                    className={`w-2 h-2 rounded-full ${importStep === "preview" ? "bg-indigo-600" : "bg-indigo-200"}`}
                  />
                </div>
              )}
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              {/* Loading */}
              {importStep === "loading" && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4" />
                  <p className="text-gray-600">Connecting to StartupKhata...</p>
                </div>
              )}

              {/* Error */}
              {importStep === "error" && (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-700 font-medium text-center">
                    {apiError}
                  </p>
                  <button
                    onClick={initApiImport}
                    className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-100"
                  >
                    Retry
                  </button>
                </div>
              )}

              {/* Select */}
              {/* {importStep === "select" && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Categories
                  </label>
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <label className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                      <span className="text-sm font-semibold text-gray-700">
                        Select All
                      </span>
                      <span className="ml-auto text-xs text-gray-400">
                        {selectedCategories.length}/{categories.length} selected
                      </span>
                    </label>

                    <div className="space-y-0 max-h-48 overflow-y-auto divide-y divide-gray-50">
                      {categories.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          No categories available
                        </div>
                      ) : (
                        categories.map((category) => (
                          <label
                            key={category.id}
                            className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(
                                category.label,
                              )}
                              onChange={() =>
                                setSelectedCategories((prev) =>
                                  prev.includes(category.label)
                                    ? prev.filter((c) => c !== category.label)
                                    : [...prev, category.label],
                                )
                              }
                              className="w-4 h-4 text-indigo-600 rounded"
                            />
                            <span className="text-sm text-gray-700">
                              {category.label}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )} */}

              {importStep === "select" && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Categories
                  </label>
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    {/* Select All row */}
                    <label className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                      <span className="text-sm font-semibold text-gray-700">
                        Select All
                      </span>
                      <span className="ml-auto text-xs text-gray-400">
                        {selectedCategories.length}/{categories.length} selected
                      </span>
                    </label>

                    {/* Category list */}
                    <div className="space-y-0 max-h-48 overflow-y-auto divide-y divide-gray-50">
                      {categories.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          No categories available
                        </div>
                      ) : (
                        categories.map((category) => (
                          <label
                            key={category.id}
                            className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(category.id)} // ← check by id
                              onChange={() =>
                                setSelectedCategories(
                                  (prev) =>
                                    prev.includes(category.id)
                                      ? prev.filter((c) => c !== category.id) // ← remove by id
                                      : [...prev, category.id], // ← store id
                                )
                              }
                              className="w-4 h-4 text-indigo-600 rounded"
                            />
                            <span className="text-sm text-gray-700">
                              {category.label} {/* ← still display label */}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Preview */}
              {importStep === "preview" && ledgerPreview && (
                <div className="space-y-4">
                  {/* Summary cards */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-indigo-50 rounded-xl p-3">
                      <p className="text-xs text-indigo-500 font-medium mb-0.5">
                        Total Records
                      </p>
                      <p className="text-2xl font-bold text-indigo-700">
                        {Array.isArray(ledgerPreview)
                          ? ledgerPreview.length
                          : (ledgerPreview?.data?.length ?? "—")}
                      </p>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-3">
                      <p className="text-xs text-purple-500 font-medium mb-0.5">
                        Categories
                      </p>
                      <p className="text-2xl font-bold text-purple-700">
                        {selectedCategories.length}
                      </p>
                    </div>
                  </div>

                  {/* Data table preview */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      Data Preview
                    </p>
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="overflow-x-auto max-h-56 overflow-y-auto">
                        {(() => {
                          const rows = Array.isArray(ledgerPreview)
                            ? ledgerPreview
                            : (ledgerPreview?.data ?? []);

                          if (rows.length === 0) {
                            return (
                              <div className="text-center py-6 text-gray-500 text-sm">
                                No data returned
                              </div>
                            );
                          }

                          const headers = Object.keys(rows[0]);

                          return (
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                  {headers.map((h) => (
                                    <th
                                      key={h}
                                      className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap"
                                    >
                                      {h}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-50">
                                {rows.slice(0, 10).map((row, i) => (
                                  <tr key={i} className="hover:bg-gray-50">
                                    {headers.map((h) => (
                                      <td
                                        key={h}
                                        className="px-3 py-2 text-gray-600 whitespace-nowrap max-w-[120px] truncate"
                                      >
                                        {String(row[h] ?? "—")}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          );
                        })()}
                      </div>
                      {/* Row count hint */}
                      {(() => {
                        const total = (
                          Array.isArray(ledgerPreview)
                            ? ledgerPreview
                            : (ledgerPreview?.data ?? [])
                        ).length;
                        return total > 10 ? (
                          <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-400 text-center">
                            Showing 10 of {total} records
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            {importStep === "select" && (
              <div className="p-6 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
                <button
                  onClick={() => {
                    setShowApiModal(false);
                    resetApiImport();
                  }}
                  className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApiImport}
                  disabled={importLoading || selectedCategories.length === 0}
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 font-medium shadow-md disabled:opacity-50"
                >
                  {importLoading ? "Fetching..." : "Next →"}
                </button>
              </div>
            )}

            {importStep === "preview" && (
              <div className="p-6 border-t border-gray-100 flex justify-between gap-3 sticky bottom-0 bg-white">
                <button
                  onClick={() => setImportStep("select")}
                  className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
                >
                  ← Back
                </button>
                <button
                  onClick={handleConvertAsClient}
                  className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:from-emerald-600 hover:to-teal-700 font-medium shadow-md flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Convert as Client
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Delete Client Confirmation Modal */}
      {confirmDeleteId && createPortal(
        <div
          className="gt-themed gt-modal-backdrop flex items-center justify-center p-4"
          style={{ position: 'fixed', inset: 0, zIndex: 100 }}
          onClick={() => !deleting && setConfirmDeleteId(null)}
        >
          <div className="gt-modal w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6" style={{ borderBottom: '1px solid var(--gt-divider)' }}>
              <div className="flex items-center gap-3">
                <span style={{
                  width: 44, height: 44, borderRadius: 14,
                  background: 'var(--gt-status-overdue-bg)',
                  color: 'var(--gt-prio-urgent-fg)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <AlertTriangle className="w-5 h-5" />
                </span>
                <h3 className="gt-display" style={{ fontSize: 18 }}>
                  Delete <em className="gt-serif-italic" style={{ color: 'var(--gt-prio-urgent-fg)' }}>Client</em>?
                </h3>
              </div>
            </div>
            <div className="p-6">
              <p className="gt-page-sub">
                This will remove <em className="gt-serif-italic" style={{ color: 'var(--gt-text-primary)' }}>"{clients.find(c => c.id === confirmDeleteId)?.clientName || 'this client'}"</em> from your directory.
              </p>
              <p className="gt-page-sub mt-2" style={{ color: 'var(--gt-prio-urgent-fg)' }}>This action cannot be undone.</p>
            </div>
            <div className="p-6 flex justify-end gap-3" style={{ borderTop: '1px solid var(--gt-divider)' }}>
              <button onClick={() => setConfirmDeleteId(null)} className="gt-btn is-ghost" disabled={!!deleting}>Cancel</button>
              <button
                onClick={performDelete}
                className="gt-btn is-danger"
                disabled={!!deleting}
                style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)',
                  color: '#ffffff',
                  borderColor: 'transparent',
                  boxShadow: '0 8px 22px -6px rgba(220, 38, 38, 0.55), 0 2px 4px -1px rgba(220, 38, 38, 0.30), inset 0 1px 0 rgba(255, 255, 255, 0.35)',
                }}
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? 'Deleting…' : 'Delete Client'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
